import React from 'react';
import {
	ChatAIMessageList,
	GenericCard,
	InboxEmptyState,
} from '@/components/dashboard';
import { Avatar, Icon, SolidDot, Typography } from '@/components/common';
import Icons from '@/const/icons';
import AppRoutes from '@/const/routes';
import clsx from 'clsx';
import { InputSend } from '@/components/chat/InputSend';
import { useRouter } from 'next/router';
import { useChatBoxClient } from '@/hooks';

export interface ChatAIProps {
	markAsRead: (id: string) => Promise<void>;
	tableData: {
		id: string;
		case_name: string;
		case_id?: string;
		is_read: boolean;
	}[];
}

export const DesktopInboxList: React.FC<ChatAIProps> = ({
	tableData,
	markAsRead,
}) => {
	const router = useRouter();
	const [idCaseSelected, setIdCaseSelected] = React.useState<string>('');
	const selectedCaseId = tableData.find(
		(caseItem) => caseItem.id === idCaseSelected
	)?.case_id;
	const {
		isLoading,
		chatMessages,
		applicationId,
		conversationId,
		streamingMessageId,
		currentBotMessage,
		containerRef,
		onSubmit,
		form,
		conversationInfo,
		inputDisabled,
		addChatMessages,
		isLoadingConversation,
		conversationPaginated,
	} = useChatBoxClient();

	const currentCaseRef = React.useRef<string | null>(null);

	React.useEffect(() => {
		const totalItems = conversationPaginated?.pages?.[0]?.totalItems;
		if (
			idCaseSelected &&
			conversationId &&
			!isLoadingConversation &&
			conversationPaginated &&
			conversationInfo?.data &&
			totalItems === 0 &&
			chatMessages.length === 0 &&
			!streamingMessageId &&
			currentCaseRef.current !== idCaseSelected
		) {
			currentCaseRef.current = idCaseSelected;
			addChatMessages('', 'bot');
		}
	}, [
		idCaseSelected,
		conversationId,
		isLoadingConversation,
		conversationPaginated,
		conversationInfo,
		chatMessages.length,
		streamingMessageId,
		addChatMessages,
	]);

	return (
		<div className="hidden md:block">
			<GenericCard className="!h-[calc(100vh-110px)] flex flex-col w-full">
				<div
					className={clsx(
						'grid grid-cols-[280px_1fr] bg-grey-2 py-2.5 px-4 rounded-t-2xl',
						tableData.length > 0 ? 'border' : ''
					)}
				>
					<Typography type="regular-12" className="text-opacity-black">
						Cases
					</Typography>
					<Typography type="regular-12" className="text-opacity-black">
						Messages
					</Typography>
				</div>
				{tableData.length > 0 ? (
					<div className="grid grid-cols-[280px_1fr] flex-1 min-h-0 border border-t-0 rounded-b-2xl">
						<div className="border-r overflow-y-auto scroll-custom">
							<ul>
								{tableData.map((caseItem) => (
									<li
										key={caseItem.id}
										className={clsx('py-2 px-3 border-b cursor-pointer', {
											'bg-green-secondary': idCaseSelected === caseItem.id,
										})}
										onClick={async () => {
											if (!caseItem.is_read) {
												await markAsRead(caseItem.id);
											}
											router.push({
												pathname: AppRoutes.CLIENTAPP_INBOX,
												query: {
													id: caseItem.id,
												},
											});
											setIdCaseSelected(caseItem.id);
										}}
									>
										<div className="flex gap-x-2 items-center justify-between">
											<div className="flex items-center gap-2">
												<SolidDot
													size="10px"
													color="bg-main-red"
													className={clsx(
														'shrink-0',
														caseItem.is_read ? 'opacity-0' : 'opacity-100'
													)}
												/>
												<Avatar
													photoUrl=""
													firstname={caseItem.case_name}
													lastname=""
													className="shrink-0"
													size="small"
												/>
												<Typography type="regular-14" className="text-black">
													{caseItem.case_name}
												</Typography>
											</div>
											{/* {caseItem.count > 0 && (
												<InboxCounterBadge count={caseItem.count} />
											)} */}
										</div>
									</li>
								))}
							</ul>
						</div>
						{idCaseSelected ? (
							<div className="flex flex-col h-full relative">
								<div className="flex-1 overflow-y-auto scroll-custom px-4">
									<ChatAIMessageList
										messages={chatMessages}
										isLoading={isLoading}
										hideHeaderDate={true}
										className="max-h-[calc(100vh-400px)]"
										applicationId={applicationId}
										conversationId={conversationId}
										caseId={selectedCaseId}
										containerRef={containerRef}
										streamingMessageId={streamingMessageId}
										currentBotMessage={currentBotMessage}
									/>
									{conversationInfo?.data.status === 'CLOSED' && (
										<Typography
											type="regular-14"
											className="text-opacity-black text-center mt-2"
										>
											This conversation has been resolved and closed.
										</Typography>
									)}
								</div>

								<div className="absolute bottom-0 py-4 px-6 border-t w-full">
									<InputSend
										form={form}
										onSubmit={onSubmit}
										disabled={inputDisabled}
									/>
								</div>
							</div>
						) : (
							<div className="flex flex-col justify-center items-center px-4">
								<Icon
									src={Icons.hatGray}
									className="mb-3"
									width={60}
									height={60}
								/>
								<Typography
									type="regular-18"
									className="text-black text-center"
								>
									Choose an item from the list on the left to see the <br />{' '}
									conversation details, attachments, and history here.
								</Typography>
							</div>
						)}
					</div>
				) : (
					<InboxEmptyState />
				)}
			</GenericCard>
		</div>
	);
};
